import { useMemo } from "react";
import PropTypes from "prop-types";
import ReactApexChart from "react-apexcharts";
// @mui
import { useTheme, styled } from "@mui/material/styles";
import { Card, CardHeader, Typography } from "@mui/material";
// utils
import { fNumber } from "../../../utils/formatNumber";
// components
import { useChart } from "../../../components/chart";

// ----------------------------------------------------------------------

const CHART_HEIGHT = 372;
const LEGEND_HEIGHT = 72;

const StyledChartWrapper = styled("div")(({ theme }) => ({
  height: CHART_HEIGHT,
  marginTop: theme.spacing(5),
  "& .apexcharts-canvas svg": { height: CHART_HEIGHT },
  "& .apexcharts-canvas svg,.apexcharts-canvas foreignObject": {
    overflow: "visible",
  },
  "& .apexcharts-legend": {
    height: LEGEND_HEIGHT,
    alignContent: "center",
    position: "relative !important",
    borderTop: `solid 1px ${theme.palette.divider}`,
    top: `calc(${CHART_HEIGHT - LEGEND_HEIGHT}px) !important`,
  },
}));
// ----------------------------------------------------------------------

LanguageChart.propTypes = {
  chartData: PropTypes.array,
  duration: PropTypes.string,
};

export default function LanguageChart({ chartData, duration }) {
  const theme = useTheme();

  const safeData = useMemo(() => {
    if (!Array.isArray(chartData) || !chartData.length) return [];
    return chartData
      .map((item) => ({
        label: String(item?.label || ""),
        value:
          typeof item?.value === "number" && !Number.isNaN(item.value)
            ? item.value
            : Number(item?.value) || 0,
      }))
      .filter((item) => item.label);
  }, [chartData]);

  const total = useMemo(() => {
    return safeData.reduce((acc, curr) => acc + curr.value, 0);
  }, [safeData]);

  const hasData = safeData.length > 0 && total > 0;

  const chartSeries = useMemo(() => {
    return safeData.map((i) => i.value);
  }, [safeData]);

  const chartLabels = useMemo(() => {
    return safeData.map((i) => i.label);
  }, [safeData]);

  const chartOptions = useChart({
    colors: [
      theme.palette.primary.main,
      theme.palette.error.main,
      theme.palette.info.main,
      theme.palette.warning.main,
    ],
    labels: chartLabels,
    stroke: { colors: [theme.palette.background.paper] },
    legend: { floating: true, horizontalAlign: "center" },
    dataLabels: { enabled: true, dropShadow: { enabled: false } },
    tooltip: {
      fillSeriesColor: false,
      y: {
        formatter: (seriesName) => fNumber(seriesName),
        title: {
          formatter: (seriesName) => `${seriesName}`,
        },
      },
    },
    plotOptions: {
      pie: { donut: { labels: { show: false } } },
    },
  });

  return (
    <Card sx={{ height: "450px" }}>
      <CardHeader title="Languages Used" />
      {!hasData ? (
        <StyledChartWrapper
          dir="ltr"
          sx={{
            height: "320px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography color="text.secondary">No data</Typography>
        </StyledChartWrapper>
      ) : (
        <StyledChartWrapper dir="ltr">
          <ReactApexChart
            type="pie"
            series={chartSeries}
            options={chartOptions}
            height={280}
          />
        </StyledChartWrapper>
      )}
    </Card>
  );
}

